import { Button } from '@fsd/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Shield } from 'lucide-react';
import buffaloLogo from '@/assets/brands/buffalo.png';
import { ProductCard } from '@/components';
import { useProducts } from '@/hooks/useProducts';
import { useTranslation } from '@/i18n/hooks';
import { useInView } from '@/utils';

export function BuffaloSection() {
  const { t, locale } = useTranslation('home');
  const { products: buffaloLine, loading } = useProducts('buffalo');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const displayBuffaloLine = buffaloLine.slice(0, 3);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="buffalo-line"
      className="relative overflow-hidden bg-white py-24 sm:py-32 dark:bg-slate-900"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[8%] -right-[10%] h-[70%] w-[70%] rounded-full bg-amber-500/10 blur-[120px] dark:bg-amber-400/15"></div>
        <div className="absolute -bottom-[18%] -left-[12%] h-[62%] w-[62%] rounded-full bg-primary/5 blur-[100px] dark:bg-primary/10"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className="mb-16 md:mb-24">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-700 uppercase tracking-widest dark:text-amber-300">
                <Shield className="h-3.5 w-3.5" />
                {t('buffalo.badge')}
              </div>

              {/* Logo + nome */}
              <div
                className={`flex items-center gap-4 mb-4 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '50ms' }}
              >
                <img
                  src={buffaloLogo}
                  alt="Buffalo"
                  className="h-12 w-auto object-contain dark:brightness-110"
                />
                <h2
                  className={`text-2xl font-bold tracking-tight text-slate-700 sm:text-3xl dark:text-slate-300 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
                >
                  {t('buffalo.title')}
                </h2>
              </div>

              {/* Subtítulo */}
              <p
                className={`text-xl leading-relaxed text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '200ms' }}
              >
                {t('buffalo.subtitle')}
              </p>
            </div>

            <div
              className={`fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '400ms' }}
            >
              <Link to="/catalog" search={{ category: 'buffalo', page: 1 }}>
                <Button
                  size="lg"
                  variant="outline"
                  className="group rounded-full border-amber-500/30 px-8 py-6 text-base font-bold text-amber-700 transition-all hover:scale-105 hover:border-amber-500 hover:bg-amber-500 hover:text-white active:scale-95 dark:text-amber-300"
                >
                  {t('buffalo.viewCatalog')}
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="relative">
          {loading ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-130 w-full animate-pulse rounded-[2.5rem] bg-slate-100 dark:bg-slate-800/50"
                />
              ))}
            </div>
          ) : buffaloLine.length === 0 ? (
            <div className="relative overflow-hidden rounded-[3rem] border border-slate-200 bg-slate-50/50 p-12 text-center dark:border-slate-800 dark:bg-slate-900/50 sm:p-24">
              <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-4xl bg-white shadow-xl dark:bg-slate-800">
                <img src={buffaloLogo} alt="Buffalo" className="h-14 w-14 object-contain" />
              </div>
              <h3 className="relative mx-auto mt-8 max-w-md text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                {t('buffalo.noProducts')}
              </h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {displayBuffaloLine.map((product, index) => (
                <div
                  key={product.id}
                  className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
                  style={{ transitionDelay: `${300 + index * 150}ms` }}
                >
                  <ProductCard drone={product} locale={locale} productType="buffalo" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
